import { useCallback, useEffect, useMemo, useState } from "react";

import { atom, useAtom } from "jotai";
import { orderBy } from "lodash";
import { useToasts } from "react-toast-notifications";
import { BooleanParam, useQueryParam, withDefault } from "use-query-params";

import { useUser } from "@components/user-context";
import {
  HistoricalPeriodChoice,
  type HistoricalPeriodChoiceNew,
  useMMMDateRange,
  useMMMPeriodSelector,
} from "@features/mmm/hooks/mmm-url-state";
import { useLazyOnErrorCallback } from "@features/mmm/hooks/use-lazy-on-error-callback";
import { useNorthbeamLazyQuery } from "@hooks/http";
import { type FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix } from "@north-beam/nb-client-side";
import {
  type GenerateBauBudget,
  type GenerateBauBudgetVariables,
  type GenerateBauBudget_me_mmm_generateBauBudget,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { type ClientSetting } from "../client-setting/types";
import { GENERATE_BAU } from "../generate-bau-queries";
import { useSelectedCyclePriorPeriod } from "../hooks/use-selected-cycle-prior-period";
import { useShowReferencePeriod } from "../hooks/use-show-reference-period";

const BauBudgetAtom = atom<
  GenerateBauBudget_me_mmm_generateBauBudget | undefined
>(undefined);

const memoizedBauErrorMessageTimeAtom = atom<number>(Date.now());

export type ReferencePerformance = {
  reference_budget: {
    date: string;
    data: { [key: string]: number };
  }[];
  performance: {
    date: string;
    data: { [key: string]: number };
  }[];
};

const referenceSettingAtom = atom<ClientSetting | null>(null);
export const useReferencePerformance = () => {
  const { user } = useUser();
  const { selectedCycle } = useSelectedCycle();

  const { clientSettings } = useClientSettings();
  const [, setPeriodChoice] = useMMMPeriodSelector();
  const [memoizedBauErrorMessageTime, setMemoizedBauErrorMessageTime] = useAtom(
    memoizedBauErrorMessageTimeAtom,
  );

  const { getSelectedCyclePriorPeriod } = useSelectedCyclePriorPeriod();

  const clientSettingsWithReferencePeriod = useMemo(
    () =>
      clientSettings.filter(
        (clientSetting) =>
          clientSetting.reference_start_date &&
          clientSetting.reference_end_date,
      ),
    [clientSettings],
  );

  const [referencePeriodClientSetting, setReferencePeriodClientSetting] =
    useAtom(referenceSettingAtom);

  const [dateRange, setDateRange] = useMMMDateRange();

  const { addToast } = useToasts();

  const setClientSettingWithReferencePeriod = useCallback(
    (clientSetting: ClientSetting | null) => {
      if (!clientSetting) {
        setReferencePeriodClientSetting(null);

        return;
      }
      setReferencePeriodClientSetting(clientSetting);
      setDateRange({
        startDate: clientSetting.reference_start_date,
        endDate: clientSetting.reference_end_date,
      });
      setPeriodChoice(clientSetting.id);
    },
    [setDateRange, setPeriodChoice, setReferencePeriodClientSetting],
  );

  useEffect(() => {
    if (!clientSettingsWithReferencePeriod.length) {
      setPeriodChoice(HistoricalPeriodChoice.PREVIOUS_PERIOD);
      setClientSettingWithReferencePeriod(null);
      setDateRange(getSelectedCyclePriorPeriod());
      return;
    }

    const chosenSetting = clientSettingsWithReferencePeriod[0];
    setClientSettingWithReferencePeriod(chosenSetting);
  }, [
    clientSettingsWithReferencePeriod,
    getSelectedCyclePriorPeriod,
    setClientSettingWithReferencePeriod,
    setDateRange,
    setPeriodChoice,
  ]);

  const enableMmmReferencePeriod = useShowReferencePeriod();

  const [budgetMixes] = useBudgetMixes();

  const [isForecastSpend, setIsForecastSpend] = useQueryParam(
    "is-forecast-spend",
    withDefault(BooleanParam, budgetMixes.length > 0),
  );

  const {
    loadData: loadBauBudget,
    data: { loading: isBauBudgetLoading, error: bauBudgetError },
  } = useNorthbeamLazyQuery<GenerateBauBudget, GenerateBauBudgetVariables>(
    GENERATE_BAU,
    {
      onCompleted: (data) => setBauBudget(data.me.mmm?.generateBauBudget),
      doNotRaiseErrorOnFailure: true,
    },
  );

  useLazyOnErrorCallback(
    bauBudgetError || null,
    () => {
      addToast("Forecasting failed, please try again later", {
        appearance: "error",
      });
      setIsForecastSpend(false);
      setMemoizedBauErrorMessageTime(Date.now() + 1000);
    },
    memoizedBauErrorMessageTime,
  );

  const [bauBudget, setBauBudget] = useAtom(BauBudgetAtom);

  useEffect(() => {
    if (
      enableMmmReferencePeriod &&
      dateRange?.endDate &&
      dateRange?.startDate
    ) {
      loadBauBudget({
        variables: {
          input: {
            client_id: user.displayUserId,
            reference_start_date: dateRange.startDate,
            reference_end_date: dateRange.endDate,
            cycle_id: selectedCycle.id,
          },
        },
      });
    } else {
      setBauBudget(undefined);
    }
  }, [
    enableMmmReferencePeriod,
    dateRange?.startDate,
    dateRange?.endDate,
    selectedCycle.id,
    user.displayUserId,
    loadBauBudget,
    setBauBudget,
  ]);

  return {
    bauBudget,
    isBauBudgetLoading,
    isForecastSpend,
    setIsForecastSpend,
    clientSettingsWithReferencePeriod,
    setClientSettingWithReferencePeriod,
    referencePeriodClientSetting,
  };
};

type Breakdown = {
  key: string;
  value: string;
  __typename: "Breakdown";
};

export const transformBauBudgetToHistoricalBudget = ({
  historical,
  bauBudget,
}: {
  historical: FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix;
  bauBudget: GenerateBauBudget_me_mmm_generateBauBudget;
}) => {
  // Create a map of breakdowns using data_column_name as the key
  const breakdownMap = historical.data.spend_data.reduce(
    (acc, { data_column_name, breakdowns }) => {
      acc[data_column_name] = breakdowns;
      return acc;
    },
    {} as Record<string, Breakdown[]>,
  );

  const spend_data = bauBudget.data.aggregated_data.spend_data
    .filter(({ data_column_name }) => breakdownMap[data_column_name])
    .map(({ data_column_name, spend }) => ({
      __typename: "BudgetMixSpendDatum" as const,
      spend,
      data_column_name,
      breakdowns: breakdownMap[data_column_name],
    }));

  const kpi_data = Object.entries(
    bauBudget.data.aggregated_data.summary_data.kpi_data,
  ).reduce((acc, [key, { values }]: any) => {
    acc[key] = values.median;
    return acc;
  }, {} as Record<string, number>);

  return {
    ...historical,
    data: {
      ...bauBudget.data.aggregated_data,
      spend_data,
      summary_data: {
        ...historical.data.summary_data,
        kpi_data,
        spend: bauBudget.data.aggregated_data.summary_data.spend,
      },
    },
  };
};
